import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { EmptyState, ErrorBanner, LimitPill, Spinner } from '../components/ui';
import { api } from '../lib/api';

/**
 * Per-account view of consumed free-advert slots.
 *
 * Only branches that are in use or already exhausted are listed — the full
 * mapping is ~110 rules and mostly zeroes, which would bury the signal.
 */
export function LimitsPage() {
  const accounts = useQuery({ queryKey: ['accounts'], queryFn: api.accounts.list });
  const [accountId, setAccountId] = useState<number | null>(null);

  useEffect(() => {
    if (accountId === null && accounts.data?.length) setAccountId(accounts.data[0]!.id);
  }, [accounts.data, accountId]);

  const overview = useQuery({
    queryKey: ['limits-overview', accountId],
    queryFn: () => api.limits.overview(accountId!),
    enabled: accountId !== null,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Publishing limits</h1>
        <p className="mt-1 text-sm text-slate-400">
          Free "Частни обяви" quotas from <code>limits.csv</code>, bound to live OLX categories. A slot is released only
          when its window elapses — archiving an advert early does not return it.
        </p>
      </div>

      <div className="card max-w-sm p-4">
        <label className="label" htmlFor="limits-account">
          Account
        </label>
        <select
          id="limits-account"
          className="input"
          value={accountId ?? ''}
          onChange={(event) => setAccountId(Number(event.target.value))}
        >
          {accounts.data?.map((account) => (
            <option key={account.id} value={account.id}>
              {account.label ?? account.name}
            </option>
          ))}
        </select>
      </div>

      {overview.isError ? <ErrorBanner error={overview.error} onRetry={() => overview.refetch()} /> : null}

      <div className="card overflow-hidden">
        {overview.isLoading ? (
          <div className="flex justify-center py-16">
            <Spinner className="h-6 w-6 text-slate-500" />
          </div>
        ) : overview.data && overview.data.length > 0 ? (
          <table className="w-full">
            <thead className="border-b border-border-subtle bg-surface-3/50">
              <tr>
                <th className="th">Category branch</th>
                <th className="th">CSV rule</th>
                <th className="th">Window</th>
                <th className="th">Usage</th>
                <th className="th">Mapping</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-subtle/60">
              {overview.data.map((scope) => (
                <tr key={scope.scopeId} className="hover:bg-surface-3/30">
                  <td className="td max-w-md truncate" title={scope.categoryPath}>
                    {scope.categoryPath}
                  </td>
                  <td className="td font-mono text-xs text-slate-400">{scope.rawLimit}</td>
                  <td className="td text-slate-400">{scope.windowDays} days</td>
                  <td className="td">
                    <LimitPill
                      used={scope.used}
                      quota={scope.quota}
                      windowDays={scope.windowDays}
                      unlimited={scope.quota === null}
                    />
                  </td>
                  <td className="td">
                    <span
                      className={
                        scope.matchMethod === 'exact'
                          ? 'badge border border-slate-700 bg-slate-800 text-slate-400'
                          : 'badge border border-amber-900/60 bg-amber-950/40 text-amber-300'
                      }
                      title={
                        scope.matchMethod === 'exact'
                          ? 'CSV name matched the OLX category name exactly'
                          : 'Matched by similarity — verify this mapping is correct'
                      }
                    >
                      {scope.matchMethod}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            title="No limits consumed on this account"
            hint="Rows appear here once adverts are published, or after Sync from OLX backfills existing ones. If this stays empty, run Resolve limits on the Accounts tab."
          />
        )}
      </div>
    </div>
  );
}
